"use strict";
/*
 * Copyright (C) 2017  Universidade de Aveiro, DETI/IEETA, Bioinformatics Group - http://bioinformatics.ua.pt/
 *
 * This file is part of Dicoogle/dicoogle-client-js.
 *
 * Dicoogle/dicoogle-client-js is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Dicoogle/dicoogle-client-js is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with Dicoogle.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.dicoogleClient = exports.DicoogleAccess = void 0;
/**
 * Dicoogle Service Wrapper
 */
const endpoints_1 = __importDefault(require("./endpoints"));
const socket_1 = require("./socket");
const service_1 = require("./service");
const tasks_1 = require("./tasks");
const users_1 = require("./users");
const util_1 = require("./util");
const presets_1 = require("./presets");
// private variables of the module
/**@private
 */
let socket_;
/** Indexer settings fields
 * @enum {string}
 */
const IndexerSettings = Object.freeze({
    /** The path to the directory to watch. type: string */
    PATH: 'path',
    /** Whether to index zip files. type: boolean */
    ZIP: 'zip',
    /** The percentage of indexation effort (from 0 to 100). type: number */
    EFFORT: 'effort',
    /** Whether to save and index thumbnails. type: boolean */
    INDEX_THUMBNAIL: 'thumbnail',
    /** The size of generated thumbnails in pixels. type: number */
    THUMBNAIL_SIZE: 'thumbnailSize',
    /** Listen for changes in the directory for automatic indexation. type: boolean */
    WATCHER: 'watcher',
});
/** Service settings fields
 * @enum {string}
 */
const ServiceSettings = Object.freeze({
    /** The service's port. type: number (integer) */
    PORT: 'path',
    /** Whether to start the service on server launch. type: boolean */
    AUTOSTART: 'autostart',
});
/** Client and main entrypoint to the Dicoogle web API.
 *
 * Obtain an instance of this type by calling the default export
 * or the export named `dicoogleClient`.
 */
class DicoogleAccess {
    /**
     * @private Please use the `dicoogleClient` function
     */
    constructor() {
        this.Endpoints = endpoints_1.default;
        this.IndexerSettings = IndexerSettings;
        this.ServiceSettings = ServiceSettings;
        // do nothing
    }
    search(query, arg1 = {}, arg2) {
        let callback, options;
        if (!arg2 && (typeof arg1 === 'function' || arg1 === undefined)) {
            callback = arg1;
            options = {};
        }
        else {
            callback = arg2;
            options = arg1;
        }
        const provider = options.provider || options.providers;
        const keyword = typeof options.keyword === 'boolean' ? options.keyword : !!query.match(/[^\s\\]:\S/);
        const { field, psize, offset } = options;
        return util_1.andCall(this.request('GET', endpoints_1.default.SEARCH)
            .query({
            query,
            keyword,
            field,
            provider,
            psize,
            offset
        }).then(res => res.body), callback);
    }
    searchDIM(query, options, callback) {
        if (!callback && (typeof options === 'function' || options === undefined)) {
            callback = options;
            options = {};
        }
        const provider = options.provider || options.providers;
        const keyword = typeof options.keyword === 'boolean' ? options.keyword : !!query.match(/[^\s\\]:\S/);
        const { psize, offset, depth } = options;
        return util_1.andCall(this.request(endpoints_1.default.SEARCH_DIM)
            .query({
            query,
            keyword,
            provider,
            psize,
            offset,
            depth
        }).then(res => res.body), callback);
    }
    dump(uid, arg1, arg2) {
        let callback, provider;
        if (typeof arg1 === 'function' && !arg2 || arg1 === undefined) {
            callback = arg1;
            provider = undefined;
        }
        else {
            callback = arg2;
            provider = arg1;
        }
        return util_1.andCall(this.request('GET', endpoints_1.default.DUMP).query({
            uid, provider
        }).then(res => res.body), callback);
    }
    issueExport(query, fields, options, callback) {
        if (typeof options === 'function' && !callback) {
            callback = options;
            options = {};
        }
        options = options !== null && options !== void 0 ? options : {};
        fields = [].concat(fields);
        const qs = {
            query, fields: JSON.stringify(fields)
        };
        if (typeof options.keyword === 'boolean') {
            qs.keyword = options.keyword;
        }
        if (options.provider) {
            qs.providers = options.provider;
        }
        return util_1.andCall(socket_.post(endpoints_1.default.EXPORT)
            .query(qs)
            .then((resp) => {
            const outcome = JSON.parse(resp.text);
            if (!outcome || typeof outcome.uid !== 'string') {
                return Promise.reject(new Error("invalid output from server"));
            }
            return outcome.uid;
        }), callback);
    }
    getProviders(arg0, arg1) {
        let type, callback;
        if (typeof arg0 === 'function' && !arg1) {
            type = 'query';
            callback = arg0;
        }
        else {
            type = arg0;
            callback = arg1;
        }
        return util_1.andCall(this.request(endpoints_1.default.PROVIDERS)
            .query({ type })
            .then(res => res.body), callback);
    }
    /**
     * Retrieve a list of query provider plugins
     * @param callback the callback function
     */
    getQueryProviders(callback) {
        return this.getProviders('query', callback);
    }
    /**
     * Retrieve a list of index provider plugins
     * @param callback the callback function
     */
    getIndexProviders(callback) {
        return this.getProviders('index', callback);
    }
    /** Retrieve a list of storage interface plugins
     * @param callback the callback function
     */
    getStorageProviders(callback) {
        return this.getProviders('storage', callback);
    }
    index(uri, provider, callback) {
        if (typeof provider === 'function' && !callback) {
            callback = provider;
            provider = undefined;
        }
        return util_1.andCallVoid(this.request('POST', endpoints_1.default.INDEX).query({
            uri,
            plugin: provider
        }), callback);
    }
    /** Retrieve the Dicoogle server's log text.
     * @param callback the callback function
     */
    getRawLog(callback) {
        return util_1.andCall(this.request(endpoints_1.default.LOGGER)
            .type('text/plain')
            .then(res => res.text), callback);
    }
    unindex(uri, provider, callback) {
        if (typeof provider === 'function' && !callback) {
            callback = provider;
            provider = undefined;
        }
        if (Array.isArray(uri) && uri.length > 1) {
            // send URIs as form data to prevent URI from being too long
            const body = uri.map(uri => 'uri=' + encodeURIComponent(uri)).join('&');
            return util_1.andCallVoid(this.request('POST', endpoints_1.default.UNINDEX)
                .type('form')
                .query({ provider })
                .send(body), callback);
        }
        else {
            return util_1.andCallVoid(this.request('POST', endpoints_1.default.UNINDEX)
                .query({
                uri,
                provider
            }), callback);
        }
    }
    /** Request that the file at the given URI is permanently removed. The operation, unlike index(), is not recursive.
     * Indices will not be updated, hence the files should be unindexed manually if so is intended.
     * @param uri a URI or array of URIs representing the files to be removed
     * @param callback the function to call on completion
     */
    remove(uri, callback) {
        if (Array.isArray(uri) && uri.length > 1) {
            // send URIs as form data to prevent URI from being too long
            return util_1.andCallVoid(this.request('POST', endpoints_1.default.REMOVE)
                .type('form')
                .send(uri.map(uri => 'uri=' + encodeURIComponent(uri)).join('&')), callback);
        }
        else {
            return util_1.andCallVoid(this.request('POST', endpoints_1.default.REMOVE)
                .query({
                uri
            }), callback);
        }
    }
    /** Retrieve the running Dicoogle version.
     * @param callback the callback function
     */
    getVersion(callback) {
        return util_1.andCall(this.request(endpoints_1.default.VERSION).then(res => res.body), callback);
    }
    /** Retrieve information about currently installed web UI plugins.
     * @param slotId the identifiers of slots to contemplate,
     *        retrieve all kinds if empty or null
     * @param callback the callback function
     */
    getWebUIPlugins(slotId, callback) {
        return util_1.andCall(this.request(endpoints_1.default.WEBUI)
            .query(slotId ? { 'slot-id': slotId } : {})
            .then((res) => {
            const outcome = res.body;
            if (!outcome || !outcome.plugins) {
                return Promise.reject(new Error("invalid output from server"));
            }
            const { plugins } = outcome;
            return plugins.map(p => {
                p.slotId = p['slot-id'] || p.dicoogle['slot-id'];
                p.moduleFile = p['module-file'] || p.dicoogle['module-file'];
                if (!p.caption) {
                    p.caption = p.dicoogle.caption;
                }
                if (!p.roles) {
                    p.roles = p.dicoogle.roles;
                }
                return p;
            });
        }), callback);
    }
    getPlugins(type, callback) {
        let req;
        if (typeof type === 'string') {
            req = this.request([endpoints_1.default.PLUGINS, type]);
        }
        else {
            if (typeof type === 'function' && !callback) {
                callback = type;
            }
            req = this.request(endpoints_1.default.PLUGINS);
        }
        return util_1.andCall(req.then(res => res.body), callback);
    }
    /** Enable a plugin.
     *
     * **Note:** Requires Dicoogle 3.
     *
     * @param type the type of plugin
     * @param name the name of the plugin
     * @param callback the callback function
     */
    enablePlugin(type, name, callback) {
        return util_1.andCall(this.request('POST', [endpoints_1.default.PLUGINS, type, name, 'enable'])
            .then((_) => { }), callback);
    }
    /** Disable a plugin.
     *
     * **Note:** Requires Dicoogle 3.
     *
     * @param type the type of plugin
     * @param name the name of the plugin
     * @param callback the callback function
     */
    disablePlugin(type, name, callback) {
        return util_1.andCall(this.request('POST', [endpoints_1.default.PLUGINS, type, name, 'disable'])
            .then((_) => { }), callback);
    }
    /**
     * [EXPERTS] Retrieve the authentication token. This token is ephemeral and may expire after some time.
     * This method is synchronous.
     * Use it only when you know what you are doing.
     * @returns the user's current authentication token, null if the user is not authenticated
     */
    getToken() {
        return socket_.getToken();
    }
    /**
     * [EXPERTS] Assign the module's session token internally. This method is synchronous.
     * Use it only when you know what you are doing. When restoring a previous (but still
     * living) session, please prefer [@link restoreSession] instead.
     *
     * @param token the same user's token of a previous session
     */
    setToken(token) {
        if (typeof token === 'string') {
            socket_.setToken(token);
        }
    }
    /**
     * [EXPERTS] Clear this object's user session information. This method is synchronous.
     * Use it only when you know what you are doing.
     */
    reset() {
        socket_.reset();
    }
    /**
     * Check whether the user is authenticated to the server. Authenticated clients will hold an
     * authentication token.
     * @returns whether the user is authenticated or not.
     */
    isAuthenticated() {
        return socket_.hasToken();
    }
    /**
     * Get the user name of the currently authenticated user.
     * @returns {string} the unique user name, null if the use is not authenticated
     */
    getUsername() {
        return socket_.getUsername();
    }
    /**
     * Get the names of the roles assigned to this user.
     * @returns an array of role names, null if the user is not authenticated
     */
    getRoles() {
        return socket_.getRoles();
    }
    /**
     * Manually log in to Dicoogle using the given credentials.
     * @param username the unique user name for the client
     * @param password the user's password for authentication
     * @param callback the callback function providing the authentication token and user information
     */
    login(username, password, callback) {
        return util_1.andCall(socket_.login(username, password), callback);
    }
    /**
     * Restore a living Dicoogle session identified by the given token.
     * @param token the same user's token of a previous session
     * @param callback the callback function providing user information
     */
    restoreSession(token, callback) {
        return util_1.andCall(socket_.restore(token), callback);
    }
    /**
     * Log out from the server.
     * @param callback the callback function
     */
    logout(callback) {
        return util_1.andCall(socket_.logout(), callback);
    }
    getIndexerSettings(field, callback) {
        if (typeof field === 'function' && !callback) {
            callback = field;
            field = undefined;
        }
        const url = [endpoints_1.default.INDEXER_SETTINGS];
        let all = true;
        if (typeof field === 'string') {
            all = false;
            url.push(encodeURIComponent(field));
            /* istanbul ignore next */
            if (process.env.NODE_ENV !== 'production') {
                /* eslint-disable no-console */
                const values = Object.keys(IndexerSettings).map(k => IndexerSettings[k]); // values()
                if (values.indexOf(field) === -1) {
                    console.error(`Warning: Attempting to get unrecognized indexer setting '${field}'.`);
                }
                /* eslint-enable no-console */
            }
        }
        return util_1.andCall(socket_.get(url).then((res) => {
            let out;
            if (all) {
                if (Object.getOwnPropertyNames(res.body).length === 0) {
                    out = JSON.parse(res.text);
                }
                else {
                    out = res.body;
                }
                if ('effort' in out)
                    out.effort = +out.effort;
                if ('thumbnailSize' in out)
                    out.thumbnailSize = +out.thumbnailSize;
            }
            else {
                out = res.text;
                if (field === 'effort' || field === 'thumbnailSize') {
                    out = +out;
                }
                if (field === 'watcher' || field === 'thumbnail' || field === 'zip') {
                    out = (out === 'true');
                }
            }
            return out;
        }), callback);
    }
    setIndexerSettings(fields, value, callback) {
        if (typeof fields === 'string') {
            const field = fields === 'thumbnail' ? 'saveThumbnail' : fields;
            fields = {};
            fields[field] = value;
        }
        else {
            fields.saveThumbnail = fields.saveThumbnail || fields.thumbnail;
            callback = value;
        }
        /* istanbul ignore next */
        if (process.env.NODE_ENV !== 'production') {
            const values = Object.keys(IndexerSettings).map(k => IndexerSettings[k]);
            values.push('saveThumbnail');
            for (const field in fields) {
                if (values.indexOf(field) === -1) {
                    /* eslint-disable no-console */
                    console.error(`Warning: Attempting to set unrecognized indexer setting '${field}'.`);
                    /* eslint-enable no-console */
                }
            }
        }
        return util_1.andCallVoid(this.request('POST', endpoints_1.default.INDEXER_SETTINGS)
            .query(fields), callback);
    }
    /** Get the list of current transfer syntax settings available.
     * @param callback the callback function
     */
    getTransferSyntaxSettings(callback) {
        return util_1.andCall(socket_.get(endpoints_1.default.TRANSFER_SETTINGS)
            .then((res) => {
            // parsing the contents as JSON manually because
            // old Dicoogle versions would not indicate the content type  
            return JSON.parse(res.text);
        }), callback);
    }
    /** Set (or reset) an option of a particular transfer syntax.
     * @param uid the unique identifier of the transfer syntax
     * @param option the name of the option to modify
     * @param value whether to set (true) or reset (false) the option
     * @param callback the callback function
     */
    setTransferSyntaxOption(uid, option, value, callback) {
        return util_1.andCallVoid(this.request('POST', endpoints_1.default.TRANSFER_SETTINGS)
            .query({ uid, option, value }), callback);
    }
    /** Retrieve the AE title of the Dicoogle archive.
     * @param callback the callback function
     */
    getAETitle(callback) {
        return util_1.andCall(this.request(endpoints_1.default.DICOM_AETITLE_SETTINGS)
            .then((res) => {
            const outcome = res.body;
            const ae = outcome && outcome.aetitle;
            if (!ae) {
                return Promise.reject(new Error("Missing server content"));
            }
            return ae;
        }), callback);
    }
    /** Redefine the AE title of the Dicoogle archive.
     * @param aetitle a valid AE title for the PACS archive
     * @param callback the callback function
     */
    setAETitle(aetitle, callback) {
        return util_1.andCallVoid(this.request('PUT', endpoints_1.default.DICOM_AETITLE_SETTINGS)
            .query({ aetitle }), callback);
    }
    request(method, uri) {
        if (!uri) {
            uri = method;
            method = 'GET';
        }
        return socket_.request(method, uri);
    }
    /** Obtain the base URL of all Dicoogle services.
     * This method is synchronous.
     * @returns {string} the currently configured base endpoint of Dicoogle
     */
    getBase() {
        return socket_.getBase();
    }
    /** Obtain a URL pointing to an item's thumbnail.
     * This function is synchronous.
     *
     * @param id a SOPInstanceUID or URI of the item
     * @param frame the frame number, if applicable
     * @returns the full URL to the thumbnail
     */
    getThumbnailUrl(id, frame) {
        let qs = util_1.isDicomUUID(id) ? `SOPInstanceUID=${id}` : `uri=${id}`;
        if (typeof frame === 'number') {
            qs += '&frame=' + frame;
        }
        return `${socket_.getBase()}/dic2png?thumbnail=true&${qs}`;
    }
    /** Obtain a URL pointing to an item's quick preview of the image.
     * This function is synchronous.
     *
     * @param id a SOPInstanceUID or URI of the item
     * @param frame the frame number, if applicable
     * @returns the full URL to the preview
     */
    getPreviewUrl(id, frame) {
        let qs = util_1.isDicomUUID(id) ? `SOPInstanceUID=${id}` : `uri=${id}`;
        if (typeof frame === 'number') {
            qs += '&frame=' + frame;
        }
        return `${socket_.getBase()}/dic2png?${qs}`;
    }
}
exports.DicoogleAccess = DicoogleAccess;
/** @private singleton module, do not use */
DicoogleAccess.__MODULE__ = new DicoogleAccess();
/** @private singleton module */
const m = DicoogleAccess.__MODULE__;
/**
 * Initialize the Dicoogle access object, which can be used multiple times.
 *
 * @param url the controller service's base url, can be null iif the endpoint is the browser context's host or the access object is already created
 * @param options a set of options regarding service access and user authentication
 * @returns a singleton dicoogle service access object
 */
function dicoogleClient(url, options = {}) {
    if (socket_ && (!url || url === socket_.getBase())) {
        return m;
    }
    if (url && url !== '') {
        if (url.indexOf('://') === -1) {
            url = (options.secure ? 'https://' : 'http://') + url;
        }
    }
    socket_ = new socket_1.Socket(url);
    if (typeof options.token === 'string') {
        socket_.setToken(options.token);
    }
    m.tasks = new tasks_1.Tasks(socket_);
    m.storage = new service_1.StorageService(socket_);
    m.queryRetrieve = new service_1.QueryRetrieveService(socket_);
    m.users = new users_1.UserService(socket_);
    m.presets = new presets_1.Presets(socket_);
    return m;
}
exports.dicoogleClient = dicoogleClient;
exports.default = dicoogleClient;
//# sourceMappingURL=index-module.js.map